import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faCircleCheck, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { ColumnConfig } from "../../../../List/schema/columnConfig";

interface SnippetListPaneProps {
	snippets: ColumnConfig[];
	selectedColumnId?: number | null;
	insertedColumnIds: number[];
	isCreatingNew: boolean;
	draftName?: string;
	isDirty?: boolean;
	onSelect: (snippet: ColumnConfig) => void;
	onCreateNew: () => void;
}

const activeItemClasses =
	"bg-blue-action-200 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:bg-blue-action before:rounded-r";

const UnsavedBadge: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-status-warning-light text-text-warning">
			{t("aiSnippet.modal.unsaved")}
		</span>
	);
};

export const SnippetListPane: FunctionComponent<SnippetListPaneProps> = ({
	snippets,
	selectedColumnId,
	insertedColumnIds,
	isCreatingNew,
	draftName,
	isDirty = false,
	onSelect,
	onCreateNew,
}) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState<string>("");

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();

		if (!q) {
			return snippets;
		}

		return snippets.filter((s) => s.name?.toLowerCase().includes(q));
	}, [snippets, search]);

	return (
		<div className="w-[268px] flex-shrink-0 border-r border-border-light bg-[#FAFBFC] flex flex-col min-h-0">
			<div className="relative px-3 pt-3.5 pb-2.5 flex-shrink-0">
				<Icon
					className="absolute left-[22px] top-1/2 -translate-y-[30%] w-3.5 h-3.5 text-text-meta"
					icon={faSearch}
				/>
				<input
					className="w-full pl-8 pr-2.5 py-2 border border-border-light rounded-md text-xs bg-white focus:outline-none focus:border-blue-action focus:shadow-[0_0_0_3px_rgba(29,61,242,0.08)]"
					placeholder={t("aiSnippet.modal.searchPlaceholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>

			<div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
				<div className="px-2 pt-2 pb-1 text-[10.5px] font-semibold text-text-meta uppercase tracking-wider">
					{t("aiSnippet.modal.library")} · {snippets.length + (isCreatingNew ? 1 : 0)}
				</div>

				{isCreatingNew && (
					<div
						className={classNames(
							"w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-text text-[13px] mb-0.5 relative text-left",
							activeItemClasses
						)}
					>
						<span className="w-[22px] h-[22px] flex-shrink-0 rounded bg-yellow text-text flex items-center justify-center">
							<Icon className="w-2.5 h-2.5" icon={faBolt} />
						</span>
						<span
							className={classNames(
								"flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-semibold",
								!draftName?.trim() && "italic font-medium text-text-meta"
							)}
						>
							{draftName?.trim() || t("aiSnippet.modal.untitled")}
						</span>
						<UnsavedBadge />
					</div>
				)}

				{filtered.length === 0 && !isCreatingNew && (
					<div className="px-2 pt-3 text-xs text-text-meta italic">
						{t("aiSnippet.modal.emptyState.title")}
					</div>
				)}

				{filtered.map((snippet) => {
					const isActive =
						!isCreatingNew && selectedColumnId != null && snippet.id === selectedColumnId;
					const isInserted = snippet.id != null && insertedColumnIds.includes(snippet.id);
					const isUnsaved = isActive && isDirty;

					return (
						<button
							key={snippet.id}
							className={classNames(
								"w-full flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer text-text text-[13px] mb-0.5 transition-colors relative text-left bg-transparent border-0",
								isActive
									? activeItemClasses
									: "hover:bg-white hover:shadow-[0_0_0_1px_var(--tw-shadow-color)] hover:shadow-border-light"
							)}
							type="button"
							onClick={() => onSelect(snippet)}
						>
							<span className="w-[22px] h-[22px] flex-shrink-0 rounded bg-yellow text-text flex items-center justify-center">
								<Icon className="w-2.5 h-2.5" icon={faBolt} />
							</span>
							<span
								className={classNames(
									"flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium",
									isActive && "font-semibold"
								)}
							>
								{snippet.name}
							</span>
							{isUnsaved && <UnsavedBadge />}
							{isInserted && (
								<Tooltip content={t("aiSnippet.modal.insertedInThisMessage")}>
									<span className="flex-shrink-0 text-blue-action flex">
										<Icon className="w-3.5 h-3.5" icon={faCircleCheck} />
									</span>
								</Tooltip>
							)}
						</button>
					);
				})}
			</div>

			<div className="flex-shrink-0 px-3 py-3.5 border-t border-border-light bg-[#FAFBFC]">
				<button
					className={classNames(
						"w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-text text-[13px] font-medium cursor-pointer transition-all hover:border-blue-action hover:text-blue-action hover:bg-blue-action-200 hover:border-solid",
						isCreatingNew && "border-blue-action text-blue-action bg-blue-action-200 border-solid"
					)}
					type="button"
					onClick={onCreateNew}
				>
					<Icon className="w-3.5 h-3.5" icon={faPlus} />
					{t("aiSnippet.modal.createNew")}
				</button>
			</div>
		</div>
	);
};
